import { memo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { api } from '../../services/api';
import type { SiteContent } from '../../types/content';
import { logger } from '../../utils/logger';

/**
 * DynamicFavicon component that sets the favicon from Sanity CMS
 * Supports multiple sizes for better browser compatibility
 */
const DynamicFavicon = () => {
	const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

	useEffect(() => {
		const fetchFavicon = async () => {
			try {
				const response = await api.getSite();
				const siteContent = response.data as SiteContent;
				if (siteContent.favicon) {
					setFaviconUrl(siteContent.favicon);
				}
			} catch (err) {
				logger.error('Error fetching favicon from site content:', err);
				// Fallback to default favicon (static file in public folder)
			}
		};

		fetchFavicon();
	}, []);

	// If no favicon from Sanity, don't render anything (fallback to static favicon in index.html)
	if (!faviconUrl) {
		return null;
	}

	return (
		<Helmet>
			{/* Standard favicon for modern browsers */}
			<link rel="icon" type="image/png" sizes="32x32" href={faviconUrl} />
			<link rel="icon" type="image/png" sizes="16x16" href={faviconUrl} />
			{/* Apple touch icon for iOS devices */}
			<link rel="apple-touch-icon" href={faviconUrl} />
			{/* Fallback for older browsers */}
			<link rel="shortcut icon" href={faviconUrl} />
		</Helmet>
	);
};

export default memo(DynamicFavicon);
